import { useEffect, useState, useCallback, useRef } from "react";
import { debugLog } from "../common/utils/debug";
import { connectionManager } from "../services/session-connection-manager.service";
import { SOCKET_EVENTS } from "../common/constants/app.constants";
import { createStateUpdater } from "../common/utils/state";
import { isManualSendInProgress, setManualSendInProgress } from "../services/session-connection-event-handlers";

interface TranscriptionState {
  interimText: string;
  finalText: string;
  isStreaming: boolean;
}

interface UseStreamingTranscriptionOptions {
  onFinalTranscription?: (text: string) => void;
  audioState?: { isSpeaking: boolean };
}

/**
 * Hook to manage real-time streaming transcription
 */
export const useStreamingTranscription = (
  options?: UseStreamingTranscriptionOptions
) => {
  const [transcription, setTranscription] = useState<TranscriptionState>({
    interimText: "",
    finalText: "",
    isStreaming: false,
  });

  // Use ref to track accumulated text to avoid stale closure issues
  const accumulatedTextRef = useRef<string>("");
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to clear the debounce timer
  const clearDebounceTimer = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  // Helper to reset transcription state to idle
  const resetTranscriptionState = useCallback(() => {
    setTranscription({
      interimText: "",
      finalText: "",
      isStreaming: false,
    });
  }, []);

  const clearTranscription = useCallback(() => {
    debugLog("🧹 [Transcription] clearTranscription called");
    debugLog("🧹 [Transcription] Accumulated text being cleared:", accumulatedTextRef.current);
    debugLog("🧹 [Transcription] Has pending debounce timer:", !!debounceTimerRef.current);
    resetTranscriptionState();
    accumulatedTextRef.current = "";
    clearDebounceTimer();
  }, [resetTranscriptionState, clearDebounceTimer]);

  useEffect(() => {
    const handleStreamingStarted = (data: any) => {
      setTranscription((prev) => ({
        ...prev,
        isStreaming: true,
        interimText: "",
        finalText: "",
      }));
      accumulatedTextRef.current = ""; // Reset accumulated text when starting new speech
    };

    const handleStreamingStopped = (data: any) => {
      debugLog("🛑 [Transcription] handleStreamingStopped called");
      debugLog("🛑 [Transcription] Timestamp:", Date.now());
      debugLog("🛑 [Transcription] isManualSendInProgress:", isManualSendInProgress());
      debugLog("🛑 [Transcription] Accumulated text:", accumulatedTextRef.current);

      // Skip if manual send is in progress - prevents duplicate messages
      if (isManualSendInProgress()) {
        debugLog("🛑 [Transcription] BLOCKED - manual send in progress, clearing state");
        debugLog("🛑 [Transcription] Resetting manualSendInProgress flag (streaming stopped)");
        setManualSendInProgress(false);
        accumulatedTextRef.current = "";
        setTranscription((prev) => ({
          ...prev,
          isStreaming: false,
          interimText: "",
        }));
        return;
      }

      // Send the accumulated text when user stops speaking
      const accumulated = accumulatedTextRef.current.trim();
      if (accumulated && options?.onFinalTranscription) {
        const lower = accumulated.toLowerCase();
        if (lower !== "stop" && lower !== "stop.") {
          debugLog("🛑 [Transcription] SENDING via onFinalTranscription:", accumulated);
          options.onFinalTranscription(accumulated);
        }
      }

      setTranscription((prev) => ({
        ...prev,
        isStreaming: false,
        interimText: "",
      }));
      accumulatedTextRef.current = ""; // Clear accumulated text
    };

    const handleInterimTranscription = (data: any) => {
      const text: string = (data.text || "").trim();

      // Note: User cannot interrupt Mai while she is speaking.
      // Transcriptions during avatar speech are discarded at the backend level
      // via IsReadyForTranscription = false flag. No action needed here.
      // The microphone stays active to keep the audio pipeline warm, but
      // all speech input is filtered until the avatar finishes speaking.
      
      if (text && options?.audioState?.isSpeaking) {
        debugLog("⏭️ [Transcription] Avatar is speaking - transcription will be filtered by backend");
        // Don't interrupt the avatar - just log that transcription is being captured
        // Backend will filter this out based on IsReadyForTranscription flag
      }
      
      const lower = text.toLowerCase();
      if (lower === "stop" || lower === "stop.") {
        setTranscription((prev) => ({
          ...prev,
          interimText: "",
          isStreaming: true,
        }));
        return;
      }
      setTranscription((prev) => ({
        ...prev,
        interimText: data.text,
        isStreaming: true,
      }));
    };

    const handleFinalTranscription = (data: any) => {
      const text: string = (data.text || "").trim();
      debugLog("📝 [Transcription] handleFinalTranscription called with:", text);
      debugLog("📝 [Transcription] Timestamp:", Date.now());
      debugLog("📝 [Transcription] isManualSendInProgress:", isManualSendInProgress());

      if (!text) return;

      // Skip if manual send is in progress - prevents new debounce timers after Enter pressed
      if (isManualSendInProgress()) {
        debugLog("📝 [Transcription] BLOCKED - manual send in progress, ignoring final transcription");
        debugLog("📝 [Transcription] Resetting manualSendInProgress flag (Azure STT finished)");
        setManualSendInProgress(false);
        return;
      }

      const lower = text.toLowerCase();
      if (lower === "stop" || lower === "stop.") {
        connectionManager.stopProcessing();
        accumulatedTextRef.current = "";
        clearDebounceTimer();
        return;
      }

      // Accumulate the text in ref ONLY - don't display until sending
      const combined = accumulatedTextRef.current ? `${accumulatedTextRef.current} ${text}` : text;
      accumulatedTextRef.current = combined;
      debugLog("📝 [Transcription] Accumulated text now:", accumulatedTextRef.current);

      // Show accumulated text in the input field so user sees what they're saying
      // This provides visual feedback that their speech is being captured
      setTranscription((current) => ({
        ...current,
        interimText: combined, // Show accumulated text as interim (appears in input box)
        finalText: "",         // Don't set finalText - that triggers send
      }));

      // Clear any existing debounce timer
      clearDebounceTimer();

      // Set a new debounce timer - if no more transcription comes in 500ms, send the message
      // Azure STT already has 1.5s silence detection, so we just need minimal debounce to catch rapid finals
      const timerStartTime = Date.now();
      debugLog("📝 [Transcription] Starting 500ms debounce timer at:", timerStartTime);
      debounceTimerRef.current = setTimeout(() => {
        debugLog("⏱️ [Transcription] Debounce timer FIRED at:", Date.now());
        debugLog("⏱️ [Transcription] Timer was set at:", timerStartTime);
        debugLog("⏱️ [Transcription] isManualSendInProgress:", isManualSendInProgress());
        debugLog("⏱️ [Transcription] Accumulated text:", accumulatedTextRef.current);

        // Skip if manual send is in progress - prevents duplicate messages
        if (isManualSendInProgress()) {
          debugLog("⏱️ [Transcription] BLOCKED - manual send in progress, clearing state");
          accumulatedTextRef.current = "";
          resetTranscriptionState();
          return;
        }

        const accumulated = accumulatedTextRef.current.trim();
        if (accumulated && options?.onFinalTranscription) {
          debugLog("⏱️ [Transcription] SENDING via onFinalTranscription:", accumulated);
          options.onFinalTranscription(accumulated);
        }
        accumulatedTextRef.current = "";
        resetTranscriptionState();
      }, 500); // 500ms debounce - just enough to catch multi-part finals from Azure STT
    };

    connectionManager.on(
      SOCKET_EVENTS.STREAMING_STARTED,
      handleStreamingStarted
    );
    connectionManager.on(
      SOCKET_EVENTS.STREAMING_STOPPED,
      handleStreamingStopped
    );
    connectionManager.on(
      SOCKET_EVENTS.TRANSCRIPTION_INTERIM,
      handleInterimTranscription
    );
    connectionManager.on(
      SOCKET_EVENTS.TRANSCRIPTION_FINAL,
      handleFinalTranscription
    );

    return () => {
      connectionManager.off(
        SOCKET_EVENTS.STREAMING_STARTED,
        handleStreamingStarted
      );
      connectionManager.off(
        SOCKET_EVENTS.STREAMING_STOPPED,
        handleStreamingStopped
      );
      connectionManager.off(
        SOCKET_EVENTS.TRANSCRIPTION_INTERIM,
        handleInterimTranscription
      );
      connectionManager.off(
        SOCKET_EVENTS.TRANSCRIPTION_FINAL,
        handleFinalTranscription
      );
    };
  }, [options]);

  return { ...transcription, clearTranscription };
};
